import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Handshake, Plus, Search, Trash2, Edit2, X, Save, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Pill } from "@/components/badges";

const STAGES = [
  "Prospect Qualification",
  "Initial Presentation",
  "Formal Bid",
  "Decision Pending",
  "Closed Won - Partnership Live",
  "Closed Lost",
];
const CLOSED = ["Closed Won - Partnership Live", "Closed Lost"];

const STAGE_STYLES = {
  "Prospect Qualification": "bg-slate-100 text-slate-700 border border-slate-200",
  "Initial Presentation": "bg-sky-50 text-sky-700 border border-sky-200",
  "Formal Bid": "bg-indigo-50 text-indigo-700 border border-indigo-200",
  "Decision Pending": "bg-amber-50 text-amber-700 border border-amber-200",
  "Closed Won - Partnership Live": "bg-emerald-50 text-emerald-700 border border-emerald-200",
  "Closed Lost": "bg-rose-50 text-rose-700 border border-rose-200",
};

const emptyForm = {
  partner_name: "",
  contact_person: "",
  email: "",
  phone: "",
  website: "",
  category: "",
  stage: "Prospect Qualification",
  assigned_to: "",
  notes: "",
  next_step: "",
  next_step_date: "",
};

export default function PartnershipPipeline() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [overview, setOverview] = useState({ by_stage: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [stage, setStage] = useState(searchParams.get("stage") || "");
  const [assignedTo, setAssignedTo] = useState(searchParams.get("assigned_to") || "");
  const [includeClosed, setIncludeClosed] = useState(searchParams.get("include_closed") !== "0");
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("eyb_partnership_pipeline_view") || "board"; } catch { return "board"; }
  });
  useEffect(() => { try { localStorage.setItem("eyb_partnership_pipeline_view", view); } catch { /* ignore */ } }, [view]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // record or null
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (stage) p.set("stage", stage);
    if (assignedTo) p.set("assigned_to", assignedTo);
    if (!includeClosed) p.set("include_closed", "0");
    setSearchParams(p, { replace: true });
  }, [q, stage, assignedTo, includeClosed]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (stage) params.stage = stage;
      if (assignedTo) params.assigned_to = assignedTo;
      params.include_closed = includeClosed;
      const [d, u, ov, cat] = await Promise.all([
        api.get("/partnership-deals", { params }),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/partnership-deals/summary/overview"),
        api.get("/partnerships").catch(() => ({ data: [] })),
      ]);
      setDeals(d.data);
      setUsers(u.data);
      setOverview(ov.data);
      setCategories(cat.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [q, stage, assignedTo, includeClosed]);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, assigned_to: user?.id || "" }); setShowForm(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      partner_name: d.partner_name || "",
      contact_person: d.contact_person || "",
      email: d.email || "",
      phone: d.phone || "",
      website: d.website || "",
      category: d.category || "",
      stage: d.stage || "Prospect Qualification",
      assigned_to: d.assigned_to || "",
      notes: d.notes || "",
      next_step: d.next_step || "",
      next_step_date: d.next_step_date || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.partner_name.trim()) { toast.error("Partner name is required"); return; }
    try {
      const payload = { ...form };
      // Strip empty strings for optional fields to keep server-side patch clean
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (!payload.stage) payload.stage = "Prospect Qualification";
      if (editing) {
        await api.patch(`/partnership-deals/${editing.id}`, payload);
        toast.success("Partnership deal updated");
      } else {
        await api.post("/partnership-deals", payload);
        toast.success("Partnership deal created");
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStage = async (id, newStage) => {
    try {
      await api.patch(`/partnership-deals/${id}`, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/partnership-deals/${toDelete.id}`, { headers: { "X-Confirm-Delete": "DELETE" } });
      toast.success("Partnership deal deleted");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8" data-testid="partnership-pipeline-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyb-label">Partnerships Pipeline</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Handshake className="w-7 h-7 text-[#1B4F8B]" />
            Partnerships Pipeline
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track potential partners through a dedicated workflow — completely separate from sales leads &amp; opportunities.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="pp-view-toggle">
            <button data-testid="pp-view-board" onClick={() => setView("board")} className={`px-3 py-1.5 text-xs font-semibold ${view === "board" ? "bg-[#29ABE2] text-white" : "bg-white text-slate-600"}`}>Board</button>
            <button data-testid="pp-view-list" onClick={() => setView("list")} className={`px-3 py-1.5 text-xs font-semibold ${view === "list" ? "bg-[#29ABE2] text-white" : "bg-white text-slate-600"}`}>List</button>
          </div>
          <Button data-testid="new-partnership-deal-btn" onClick={openCreate} className="bg-[#1B4F8B] hover:bg-[#163e6e]">
            <Plus className="w-4 h-4 mr-1" /> New Partnership Deal
          </Button>
        </div>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="pp-overview">
        <div className="eyb-card p-4">
          <div className="eyb-label">Total</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{overview.total ?? 0}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">Active</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{overview.active ?? 0}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">Live Partners</div>
          <div className="font-heading text-2xl font-bold text-emerald-700 mt-1">{overview.won ?? 0}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">Lost</div>
          <div className="font-heading text-2xl font-bold text-rose-700 mt-1">{overview.lost ?? 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="eyb-card p-4 mb-4 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="pp-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search partner, contact, email…"
            className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
          />
        </div>
        <select data-testid="pp-filter-stage" value={stage} onChange={(e) => setStage(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="pp-filter-owner" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          <option value="">All owners</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white cursor-pointer hover:bg-slate-50">
          <input
            type="checkbox"
            data-testid="pp-filter-include-closed"
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
          />
          <span className="text-slate-700">Include closed</span>
        </label>
      </div>

      {view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto" data-testid="pp-board">
          {STAGES.map((s) => {
            const items = deals.filter((d) => d.stage === s);
            return (
              <div key={s} className="bg-slate-50 rounded-xl p-3 min-h-[220px]" data-testid={`pp-stage-col-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div>
                    <div className="text-[11px] font-bold text-slate-800 leading-tight">{s}</div>
                    <div className="text-[10px] text-slate-500">{items.length}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {items.map((d) => (
                    <div key={d.id} className="bg-white rounded-lg p-3 border border-slate-200 hover:border-[#29ABE2]/50 transition-colors" data-testid={`pp-card-${d.id}`}>
                      <button onClick={() => openEdit(d)} className="text-left w-full">
                        <div className="font-semibold text-sm text-slate-900 hover:text-[#1f8ebf]">{d.partner_name}</div>
                        {d.contact_person && <div className="text-[11px] text-slate-500 mt-0.5">{d.contact_person}</div>}
                        {d.category && <div className="text-[10px] text-slate-500 mt-0.5">{d.category}</div>}
                      </button>
                      <div className="text-[11px] text-slate-500 mt-2 flex items-center justify-between">
                        <span>{usersById[d.assigned_to]?.name?.split(" ")[0] || "—"}</span>
                        {d.next_step_date && <span title="Next step">{fmtDate(d.next_step_date)}</span>}
                      </div>
                      <select
                        data-testid={`pp-card-stage-${d.id}`}
                        value={d.stage}
                        onChange={(e) => changeStage(d.id, e.target.value)}
                        className="mt-2 w-full text-[11px] border border-slate-200 rounded px-1.5 py-1 bg-white"
                      >
                        {STAGES.map((st) => <option key={st}>{st}</option>)}
                      </select>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-[11px] text-slate-400 text-center py-4">Empty</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="eyb-card overflow-hidden" data-testid="pp-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
                <th className="px-4 py-3 font-semibold">Partner</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 font-semibold">Owner</th>
                <th className="px-4 py-3 font-semibold">Next Step</th>
                <th className="px-4 py-3 font-semibold w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400 text-xs">Loading…</td></tr>
              ) : deals.length === 0 ? (
                <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">No partnership deals yet. Click <span className="font-semibold">New Partnership Deal</span> to create one.</td></tr>
              ) : deals.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`pp-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <button className="font-semibold text-slate-900 hover:text-[#1f8ebf] text-left" onClick={() => openEdit(d)}>{d.partner_name}</button>
                    {d.email && <div className="text-xs text-slate-500">{d.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{d.contact_person || "—"}<div className="text-xs text-slate-500">{d.phone || ""}</div></td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{d.category || "—"}</td>
                  <td className="px-4 py-3"><Pill className={STAGE_STYLES[d.stage]}>{d.stage}</Pill></td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{usersById[d.assigned_to]?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">
                    {d.next_step ? <div className="font-medium text-slate-800">{d.next_step}</div> : <span className="text-slate-400">—</span>}
                    {d.next_step_date && <div className="text-[11px] text-slate-500">{fmtDate(d.next_step_date)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(d)} data-testid={`pp-edit-${d.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Edit">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {isAdmin && (
                        <button onClick={() => setToDelete(d)} data-testid={`pp-delete-${d.id}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl" data-testid="pp-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Partnership Deal" : "New Partnership Deal"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update partner details, stage or next step."
                : "Add a new partner to the pipeline. This lives in its own workflow — it will not appear in Leads or Opportunities."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Partner Name *</Label>
              <Input data-testid="pp-form-partner-name" value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} placeholder="e.g. Smith &amp; Co. Accountants" />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input data-testid="pp-form-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            </div>
            <div>
              <Label>Category</Label>
              <select data-testid="pp-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                <option value="">— None —</option>
                {categories.filter((c) => c.active !== false).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="pp-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="pp-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Website</Label>
              <Input data-testid="pp-form-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
            </div>
            <div>
              <Label>Stage</Label>
              <select data-testid="pp-form-stage" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Owner</Label>
              <select data-testid="pp-form-owner" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Next Step</Label>
              <Input data-testid="pp-form-next-step" value={form.next_step} onChange={(e) => setForm({ ...form, next_step: e.target.value })} placeholder="e.g. Follow-up call" />
            </div>
            <div>
              <Label>Next Step Date</Label>
              <Input data-testid="pp-form-next-step-date" type="date" value={form.next_step_date || ""} onChange={(e) => setForm({ ...form, next_step_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea data-testid="pp-form-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button data-testid="pp-form-save" onClick={save} className="bg-[#1B4F8B] hover:bg-[#163e6e]">
              <Save className="w-4 h-4 mr-1" /> {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="pp-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this partnership deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold">{toDelete?.partner_name}</span> along with its notes, activities and tasks. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="pp-delete-confirm" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
